import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toPng, toJpeg } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { Loader2, Download, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import './TrafficLights.css';

const API = process.env.REACT_APP_BACKEND_URL;

const BAND_CLASS = { g: 'tl-g', a: 'tl-a', r: 'tl-r', n: 'tl-n', s: 'tl-s' };

function Cell({ cell, wide }) {
  const [v, b] = cell || [null, 'n'];
  return <td className={`tl-cell ${BAND_CLASS[b] || 'tl-n'} ${wide ? 'tl-wide' : ''}`}>{v === null ? '-' : v}</td>;
}

export default function TrafficLights({ area, onOpenCard }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState('rank');
  const [exporting, setExporting] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/staff/stats/${area}`);
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No report available');
      }
      setLoading(false);
    })();
  }, [area]);

  const doExport = useCallback(async (format) => {
    if (!tableRef.current) return;
    setExporting(true);
    await new Promise((r) => setTimeout(r, 60));
    try {
      const node = tableRef.current;
      const opts = { backgroundColor: '#3d3d3d', pixelRatio: 2, includeQueryParams: true };
      const fname = `${data.period.replace(/\s+/g, '-')}-Traffic-Lights-${data.team.replace(/\s+/g, '-')}`;
      if (format === 'pdf') {
        const dataUrl = await toPng(node, opts);
        const img = new Image();
        await new Promise((res) => { img.onload = res; img.src = dataUrl; });
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [img.width / 2, img.height / 2] });
        pdf.addImage(dataUrl, 'PNG', 0, 0, img.width / 2, img.height / 2);
        pdf.save(`${fname}.pdf`);
      } else {
        const dataUrl = format === 'png' ? await toPng(node, opts) : await toJpeg(node, { ...opts, quality: 0.92 });
        const a = document.createElement('a');
        a.download = `${fname}.${format}`;
        a.href = dataUrl;
        a.click();
      }
      toast.success(`Report downloaded as ${format.toUpperCase()}`);
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    }
    setExporting(false);
  }, [data]);

  if (loading) return <div className="tl-loading"><Loader2 size={20} className="tl-spin" /> Loading report…</div>;
  if (!data) return <div className="tl-none" data-testid="tl-none">No traffic light report uploaded for this area yet.</div>;

  const pctNum = (r) => (r.pct[0] === null ? -9999 : parseInt(r.pct[0]));
  const rows = [...data.rows];
  if (sortMode === 'best') rows.sort((a, b) => pctNum(b) - pctNum(a));
  if (sortMode === 'worst') rows.sort((a, b) => pctNum(a) - pctNum(b));
  if (sortMode === 'az') rows.sort((a, b) => a.name.localeCompare(b.name));

  const counts = data.rows.reduce((acc, r) => { acc[r.band] = (acc[r.band] || 0) + 1; return acc; }, {});

  return (
    <div className="tl-wrap" data-testid="traffic-lights">
      <div className="tl-toolbar">
        <div className="tl-summary">
          <span className="tl-chip tl-g" data-testid="tl-count-green">{counts.g || 0} on target</span>
          <span className="tl-chip tl-a" data-testid="tl-count-amber">{counts.a || 0} watch</span>
          <span className="tl-chip tl-r" data-testid="tl-count-red">{counts.r || 0} action needed</span>
          <span className="tl-chip tl-n">{counts.n || 0} no forecast</span>
        </div>
        <div className="tl-actions">
          <button className="tl-sort-btn" data-testid="tl-sort-btn"
            onClick={() => setSortMode((s) => ({ rank: 'best', best: 'worst', worst: 'az', az: 'rank' }[s]))}>
            <ArrowUpDown size={13} />
            {{ rank: 'Report order', best: 'Best first', worst: 'Worst first', az: 'A–Z' }[sortMode]}
          </button>
          {['png', 'jpg', 'pdf'].map((f) => (
            <button key={f} className="tl-dl-btn" onClick={() => doExport(f)} disabled={exporting} data-testid={`tl-download-${f}`}>
              {exporting ? <Loader2 size={12} className="tl-spin" /> : <Download size={12} />}
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="tl-board" ref={tableRef}>
        <div className="tl-head-row">
          <span className="tl-title">{data.period} Photographer Traffic Light Results</span>
          <span className="tl-team">{data.team}</span>
        </div>
        <table className="tl-table">
          <thead>
            <tr className="tl-grouphead">
              <th colSpan={3}></th>
              <th colSpan={4}>NET DCOS INCOME</th>
              <th colSpan={4}>% Performance against Forecast (year to date)</th>
              <th colSpan={4}>% Performance against Forecast (per quarter)</th>
            </tr>
            <tr>
              <th>#</th>
              <th>Grade<br /><span className="tl-sub">FC / ADJ</span></th>
              <th className="tl-name-h">Photographer</th>
              <th>2025<br /><span className="tl-sub">actual</span></th>
              <th>2026<br /><span className="tl-sub">forecast</span></th>
              <th>2026<br /><span className="tl-sub">actual</span></th>
              <th>%</th>
              <th>FY %</th><th>Q3 %</th><th>HY %</th><th>Q1 %</th>
              <th>Q2 %</th><th>Q1 %</th><th>Q4 %</th><th>Q3 %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.num}
                className={`tl-row ${BAND_CLASS[r.band]} has-card`}
                onClick={() => onOpenCard(r.staff_id || `row-${area}-${r.num}`)}
                title="Open photographer report"
                data-testid={`tl-row-${r.num}`}
              >
                <td className="tl-num">{r.num}</td>
                <td className={`tl-grade ${BAND_CLASS[r.band]}`}>{r.grade}</td>
                <td className={`tl-name ${BAND_CLASS[r.band]}`}>
                  {r.photo && <img src={`${API}${r.photo}`} alt="" className="tl-face" />}
                  {r.name}
                  {r.has_card && <span className="tl-card-dot" title="Performance card available" />}
                </td>
                <td className={`tl-cell ${BAND_CLASS[r.band]}`}>{r.a2025}</td>
                <td className={`tl-cell ${BAND_CLASS[r.band]}`}>{r.f2026}</td>
                <td className={`tl-cell ${BAND_CLASS[r.band]}`}>{r.a2026}</td>
                <Cell cell={r.pct} />
                <Cell cell={r.ytd.fy} /><Cell cell={r.ytd.q3} /><Cell cell={r.ytd.hy} /><Cell cell={r.ytd.q1} />
                <Cell cell={r.quarter.q2} /><Cell cell={r.quarter.q1} /><Cell cell={r.quarter.q4} /><Cell cell={r.quarter.q3} />
              </tr>
            ))}
            {data.spares.map((r) => (
              <tr key={r.num} className="tl-row tl-s">
                <td className="tl-num">{r.num}</td>
                <td className="tl-grade tl-s">{r.grade}</td>
                <td className="tl-name tl-s">{r.name}</td>
                <td className="tl-cell tl-s">{r.a2025}</td>
                <td className="tl-cell tl-s">{r.f2026}</td>
                <td className="tl-cell tl-s">{r.a2026}</td>
                {Array.from({ length: 9 }).map((_, i) => <td key={i} className="tl-cell tl-s">-</td>)}
              </tr>
            ))}
            <tr className="tl-row tl-am">
              <td colSpan={2}></td>
              <td className="tl-name tl-amgr">{data.area_manager.name}</td>
              <td className="tl-cell tl-amgr">{data.area_manager.a2025}</td>
              <td className="tl-cell tl-amgr">{data.area_manager.f2026}</td>
              <td className="tl-cell tl-amgr">{data.area_manager.a2026}</td>
              {Array.from({ length: 9 }).map((_, i) => <td key={i} className="tl-cell"></td>)}
            </tr>
            <tr className="tl-row tl-totals" data-testid="tl-totals">
              <td colSpan={2}></td>
              <td className="tl-name tl-tot">{data.totals.name}</td>
              <td className="tl-cell tl-tot">{data.totals.a2025}</td>
              <td className="tl-cell tl-tot">{data.totals.f2026}</td>
              <td className="tl-cell tl-tot">{data.totals.a2026}</td>
              <Cell cell={data.totals.pct} />
              <Cell cell={data.totals.ytd.fy} /><Cell cell={data.totals.ytd.q3} /><Cell cell={data.totals.ytd.hy} /><Cell cell={data.totals.ytd.q1} />
              <Cell cell={data.totals.quarter.q2} /><Cell cell={data.totals.quarter.q1} /><Cell cell={data.totals.quarter.q4} /><Cell cell={data.totals.quarter.q3} />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="tl-hint">Click any photographer to open their report — traffic light breakdown, performance data and quarterly questions. Grades run 1–9 (9 = best); "6 &gt; 2" means a Grade 2 photographer has taken over a Grade 6 round, so the forecast still reflects the previous grade.</p>
    </div>
  );
}
